/**
 * Which meeting phase page a given viewer belongs on.
 *
 * Every meeting card on every board used to link straight to `/pre-review`,
 * which is the Phase-02 **staff** surface. A volunteer clicking a Phase 3/4/5
 * card landed on the staff review workflow — final distribution, the three
 * audience email editors, "Open Live Progress" — and the only thing standing in
 * front of it was an `isStaff` branch inside the page. Cards route through here
 * instead, so a member is sent to the phase page that was built for them.
 *
 * Staff keep `/pre-review`: it is their review queue's landing page and it
 * self-advances through steps 1-4.
 *
 * `MeetingReport`'s own phase-enforcement effect shares the branch below via
 * `volunteerPhaseSegment`, so a card link and the redirect it lands on cannot
 * disagree and bounce the viewer between two pages.
 */

/**
 * Everything the phase decision depends on — deliberately not the meeting's id.
 * Which phase someone belongs in is a function of the event's state, so a caller
 * that only wants the segment (the redirect inside a page already on that event)
 * does not have to supply one.
 */
export interface PhaseState {
    status?: string | null;
    start_at?: string | null;
    /** Phase 4 ends when the LinkedIn recap is published, not at `end_at`. */
    phase4_published_at?: string | null;
    force_phase4?: boolean | null;
}

/** A meeting we can build a full href for. */
export interface PhaseRoutableMeeting extends PhaseState {
    id: string;
    entity_type?: string | null;
    entity_id?: number | string | null;
}

/** Phase pages, as route segments under a meeting's base href. */
export type PhaseSegment =
    | "pre-review"
    | "wait"
    | "reporting/pre"
    | "reporting/live"
    | "reporting/review";

/**
 * The phase segment a non-staff viewer belongs on for this meeting.
 *
 * - `reviewing` — the volunteer's own Phase-01 draft review, on `/pre-review`.
 * - `pending_staff_review` — waiting on staff; nothing for them to do yet.
 * - `approved` / `completed` — the reporting workspace, at the phase the clock
 *   and the Phase-4 publish put them in.
 *
 * `phase4_published_at` is often absent from list payloads. When it is, an event
 * that has started resolves to `reporting/live` and `MeetingReport` moves the
 * viewer on to `reporting/review` once it loads the real value — the same
 * behaviour as before, just arrived at one hop later.
 */
export function volunteerPhaseSegment(meeting: PhaseState): PhaseSegment | null {
    const status = (meeting.status || "").toLowerCase();

    if (status === "reviewing") return "pre-review";
    if (status === "pending_staff_review") return "wait";

    // Draft states that never reached staff — the creator still owns the draft.
    if (["draft", "submitted", "pending_review", "pending_chair_review"].includes(status)) {
        return "pre-review";
    }

    // A cancelled event has no phase to be in: every phase page renders its own
    // cancelled notice, so wherever the viewer already is, leave them there.
    // Returning a segment here would bounce them off a page that was showing them
    // exactly the right thing.
    if (status === "cancelled") return null;

    // `approved`, `completed`, and the never-persisted `live`.
    const phase4Done = status === "completed" || !!meeting.phase4_published_at;
    if (phase4Done) return "reporting/review";

    const start = meeting.start_at ? new Date(meeting.start_at) : null;
    const started = !!meeting.force_phase4 || !start || Date.now() >= start.getTime();
    return started ? "reporting/live" : "reporting/pre";
}

/** The base href for a meeting's phase pages. */
export function meetingBaseHref(
    meeting: PhaseRoutableMeeting,
    entityType: string | number,
    entityId: string | number,
): string {
    const type = meeting.entity_type ?? entityType;
    const id = meeting.entity_id ?? entityId;
    return `/dashboard/manage/groups/${type}/${id}/meetings/${meeting.id}`;
}

/**
 * Where a meeting card should link for this viewer.
 *
 * Pass `isStaff` from the viewer's *system* role (`admin`/`superadmin`/`staff`),
 * never from a group assignment: a chair or liaison is not staff, and treating
 * one as staff is what put the distribution workflow in front of a volunteer in
 * the first place.
 */
export function meetingPhaseHref(
    meeting: PhaseRoutableMeeting,
    entityType: string | number,
    entityId: string | number,
    opts: { isStaff?: boolean } = {},
): string {
    const base = meetingBaseHref(meeting, entityType, entityId);
    if (opts.isStaff) return `${base}/pre-review`;
    // A link has to go *somewhere*; for a cancelled event (no enforced phase) that
    // is the Phase-5 summary, which carries the cancellation notice.
    return `${base}/${volunteerPhaseSegment(meeting) ?? "reporting/review"}`;
}

/** True for the system roles that own the staff workflow. */
export function isStaffRole(role?: string | null): boolean {
    return ["staff", "admin", "superadmin"].includes((role || "").toLowerCase());
}
